import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Shield, Crown, User as UserIcon, Ban, CheckCircle, Trash2, ArrowUpDown } from "lucide-react";

type UserRole = 'admin' | 'paid' | 'student' | 'free';
type SortField = 'email' | 'created_at' | 'role' | 'queryCount' | 'totalTokens' | 'totalCost';
type SortOrder = 'asc' | 'desc';

interface UserWithRole {
  id: string;
  email: string;
  created_at: string;
  role: UserRole | null;
  isBanned: boolean;
  banReason: string | null;
  usage: {
    totalTokens: number;
    totalCost: number;
    queryCount: number;
  };
}

interface UsersTableProps {
  users: UserWithRole[];
  currentUserId?: string;
  onRoleChange: (userId: string, newRole: UserRole) => void;
  onBan: (userId: string, email: string) => void;
  onUnban: (userId: string, email: string) => void;
  onDelete: (userId: string, email: string) => void;
}

export function UsersTable({
  users,
  currentUserId,
  onRoleChange,
  onBan,
  onUnban,
  onDelete,
}: UsersTableProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getRoleBadge = (role: UserRole | null) => {
    if (role === 'admin') {
      return <Badge variant="destructive" className="gap-1"><Shield className="h-3 w-3" /> Admin</Badge>;
    } else if (role === 'paid') {
      return <Badge variant="default" className="gap-1"><Crown className="h-3 w-3" /> Paid</Badge>;
    } else if (role === 'student') {
      return <Badge variant="secondary" className="gap-1"><UserIcon className="h-3 w-3" /> Student</Badge>;
    } else {
      return <Badge variant="outline" className="gap-1"><UserIcon className="h-3 w-3" /> Free</Badge>;
    }
  };

  const filteredAndSortedUsers = users
    .filter(user => {
      const matchesSearch = user.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole = roleFilter === "all" || user.role === roleFilter;
      const matchesStatus = statusFilter === "all" || 
        (statusFilter === "banned" && user.isBanned) ||
        (statusFilter === "active" && !user.isBanned);
      return matchesSearch && matchesRole && matchesStatus;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'email':
          comparison = a.email.localeCompare(b.email);
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'role':
          comparison = (a.role || 'free').localeCompare(b.role || 'free');
          break;
        case 'queryCount':
          comparison = a.usage.queryCount - b.usage.queryCount;
          break;
        case 'totalTokens':
          comparison = a.usage.totalTokens - b.usage.totalTokens;
          break;
        case 'totalCost':
          comparison = a.usage.totalCost - b.usage.totalCost;
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Input
          placeholder="Search by email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="sm:max-w-xs"
        />
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="sm:w-[180px]">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="student">Student & Educator</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="sm:w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="banned">Banned</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Results count */}
      <div className="text-sm text-muted-foreground">
        Showing {filteredAndSortedUsers.length} of {users.length} users
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 font-medium"
                  onClick={() => handleSort('email')}
                >
                  Email
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 font-medium"
                  onClick={() => handleSort('role')}
                >
                  Role
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>Status</TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 font-medium"
                  onClick={() => handleSort('created_at')}
                >
                  Joined
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 font-medium"
                  onClick={() => handleSort('queryCount')}
                >
                  Queries
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 font-medium"
                  onClick={() => handleSort('totalTokens')}
                >
                  Tokens
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 font-medium"
                  onClick={() => handleSort('totalCost')}
                >
                  Cost
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAndSortedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    <div className="space-y-1">
                      <div>{user.email}</div>
                      {user.banReason && (
                        <div className="text-xs text-destructive">
                          Ban: {user.banReason}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={user.role || 'free'}
                      onValueChange={(value) => onRoleChange(user.id, value as UserRole)}
                      disabled={user.id === currentUserId}
                    >
                      <SelectTrigger className="w-[160px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="student">Student & Educator</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {user.isBanned ? (
                      <Badge variant="destructive" className="gap-1">
                        <Ban className="h-3 w-3" /> Banned
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="gap-1">
                        <CheckCircle className="h-3 w-3" /> Active
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(user.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {user.usage.queryCount}
                  </TableCell>
                  <TableCell className="text-right">
                    {user.usage.totalTokens.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    ${user.usage.totalCost.toFixed(4)}
                  </TableCell>
                  <TableCell className="text-right">
                    {user.id !== currentUserId && (
                      <div className="flex justify-end gap-2">
                        {user.isBanned ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onUnban(user.id, user.email)}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => onBan(user.id, user.email)}
                          >
                            <Ban className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => onDelete(user.id, user.email)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
